import {lastValueFrom, map, Observable} from 'rxjs';
import {CpmCObjectStoreEntryProxyApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {CpmCObjectStoreEntryProxyApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-object-store-entry-proxy.api-model';

export enum EObjectStoreEntriesLevel {
    CUSTOMER = 'customer',
    ORGANIZATION = 'organization',
    PROJECT = 'project',
    SERVICE_REQUEST = 'service_request'
}

export class ObjectStoreEntriesTransientCache {
    private _isInitialized = false;
    private readonly _entriesForLevel: Record<string, CpmCObjectStoreEntryProxyApiModel[]> = {};
    private readonly _customerUUID: string;
    private readonly _organizationUUID?: string;
    private readonly _projectUUID?: string;
    private readonly _serviceRequestUUID?: string;

    private _lowestStoreLevel: EObjectStoreEntriesLevel;
    private _inheritedEntries: CpmCObjectStoreEntryProxyApiModel[] = [];
    private _definedEntries: CpmCObjectStoreEntryProxyApiModel[] = [];

    // ===============================================================
    // ===============================================================

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get customerUUID(): string {
        return this._customerUUID;
    }
    public get organizationUUID(): string | undefined {
        return this._organizationUUID;
    }
    public get projectUUID(): string | undefined {
        return this._projectUUID;
    }
    public get serviceRequestUUID(): string | undefined {
        return this._serviceRequestUUID;
    }

    public get inheritedEntries(): CpmCObjectStoreEntryProxyApiModel[] {
        return this._inheritedEntries;
    }
    public get definedEntries(): CpmCObjectStoreEntryProxyApiModel[] {
        return this._definedEntries;
    }

    public get lowestStoreLevel(): EObjectStoreEntriesLevel {
        return this._lowestStoreLevel;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     * @param customerUUID Mandatory customer UUID
     * @param opts Options for sub links
     */
    constructor(
        customerUUID: string,
        opts: {organizationUUID?: string; projectUUID?: string; serviceRequestUUID?: string}
    ) {
        this._customerUUID = customerUUID;
        this._organizationUUID = opts.organizationUUID;
        this._projectUUID = opts.projectUUID;
        this._serviceRequestUUID = opts.serviceRequestUUID;

        if (this._serviceRequestUUID) this._lowestStoreLevel = EObjectStoreEntriesLevel.SERVICE_REQUEST;
        else if (this._projectUUID) this._lowestStoreLevel = EObjectStoreEntriesLevel.PROJECT;
        else if (this._organizationUUID) this._lowestStoreLevel = EObjectStoreEntriesLevel.ORGANIZATION;
        else this._lowestStoreLevel = EObjectStoreEntriesLevel.CUSTOMER;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the cache once
     * @returns
     */
    public async init() {
        if (this._isInitialized) return;

        const coroutines = [
            this.pullEntriesForLevel(EObjectStoreEntriesLevel.CUSTOMER),
            this.pullEntriesForLevel(EObjectStoreEntriesLevel.ORGANIZATION),
            this.pullEntriesForLevel(EObjectStoreEntriesLevel.PROJECT),
            this.pullEntriesForLevel(EObjectStoreEntriesLevel.SERVICE_REQUEST)
        ];
        for (const coroutine of coroutines) await coroutine;

        this.resolve();

        this._isInitialized = true;
    }
    /**
     * Update entries for a specific level
     * @param level Level to update
     */
    public async updateEntriesForLevel(level: EObjectStoreEntriesLevel) {
        await this.pullEntriesForLevel(level);
        this.resolve();
    }
    /**
     * Get defined on inherited entry by key
     * @param key Lookup key
     * @returns Found entry
     */
    public getDefinedOrInheritedEntry(key: string): CpmCObjectStoreEntryProxyApiModel | undefined {
        const defined = this._definedEntries.find((x) => x.key == key);
        if (defined) return defined;

        const inherited = this._inheritedEntries.find((x) => x.key == key);
        if (inherited) return inherited;

        return undefined;
    }
    /**
     * Get entry for a specific level and key
     * @param level Level to search in
     * @param key Lookup key
     * @returns Found entry
     */
    public getEntryForLevel(
        level: EObjectStoreEntriesLevel,
        key: string
    ): CpmCObjectStoreEntryProxyApiModel | undefined {
        if (!(level in this._entriesForLevel)) return undefined;

        return this._entriesForLevel[level].find((x) => x.key == key);
    }
    /**
     * Get level for an entry
     * @param entry Entry to lookup
     * @returns Found level
     */
    public getEntryLevel(entry: CpmCObjectStoreEntryProxyApiModel): EObjectStoreEntriesLevel | undefined {
        for (const level of Object.values(EObjectStoreEntriesLevel)) {
            if (this._entriesForLevel[level]?.includes(entry)) return level;
        }

        return undefined;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Pull entries for a specific level
     * @param level Level to pull
     */
    private async pullEntriesForLevel(level: EObjectStoreEntriesLevel) {
        let linkObjectType = '';
        let linkObjectUUID: string | undefined = undefined;

        switch (level) {
            case EObjectStoreEntriesLevel.CUSTOMER:
                linkObjectType = 'customer';
                linkObjectUUID = this._customerUUID;
                break;

            case EObjectStoreEntriesLevel.ORGANIZATION:
                linkObjectType = 'organization';
                linkObjectUUID = this._organizationUUID;
                break;

            case EObjectStoreEntriesLevel.PROJECT:
                linkObjectType = 'project';
                linkObjectUUID = this._projectUUID;
                break;

            case EObjectStoreEntriesLevel.SERVICE_REQUEST:
                linkObjectType = 'service_request';
                linkObjectUUID = this._serviceRequestUUID;
                break;
        }

        if (!linkObjectUUID) return;

        const entries = await lastValueFrom(this.getEntriesFor(linkObjectType, linkObjectUUID));
        this._entriesForLevel[level.toString()] = entries;
    }
    /**
     * Get entries for link object
     * @param linkObjectType Type of link object
     * @param linkObjectUUID UUID of link object
     * @returns Entries for this link object
     */
    private getEntriesFor(
        linkObjectType: string,
        linkObjectUUID: string
    ): Observable<CpmCObjectStoreEntryProxyApiModel[]> {
        return CpmCObjectStoreEntryProxyApiService.getObjectStoreEntryProxyList(
            linkObjectType,
            linkObjectUUID,
            {},
            {limit: -1, sort: 'key'}
        ).pipe(map((x) => x.data));
    }

    // ===============================================================
    // ===============================================================

    /**
     * Resolve all entries with inheritance
     */
    private resolve() {
        this._definedEntries = [];
        this._inheritedEntries = [];

        Object.values(EObjectStoreEntriesLevel).forEach((level) => {
            if (!(level in this._entriesForLevel)) return;

            const target = level == this._lowestStoreLevel ? this._definedEntries : this._inheritedEntries;
            const other = level == this._lowestStoreLevel ? this._inheritedEntries : this._definedEntries;

            for (const entry of this._entriesForLevel[level]) {
                const existingInTarget = target.findIndex((x) => x.key == entry.key);
                if (existingInTarget >= 0) target.splice(existingInTarget, 1);

                const existingInOther = other.findIndex((x) => x.key == entry.key);
                if (existingInOther >= 0) other.splice(existingInOther, 1);

                target.push(entry);
            }
        });
    }
}
